import { useMemo } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { WorkStatus, CatJobHistoryItem } from "../../../client/types.gen";
import { formatExactMoneyDollars } from "../Money";
import { formatDuration } from "../icm/util";
import { SelectedModel } from "./types";
import styles from "./CatCard.module.css";

interface RunHistoryTabProps {
  jobHistory: CatJobHistoryItem[];
  selectedModel: SelectedModel;
  viewingBatchId: string | null;
  onViewJobResults: (job: CatJobHistoryItem) => void;
  onDownloadAll: (job: CatJobHistoryItem) => void;
}

const renderStatusCell = (job: CatJobHistoryItem) => {
  const modelTag = (
    <Tag
      value={
        job.type === "spatialkat"
          ? "KatRisk"
          : job.type === "rms_elt"
            ? "RMS"
            : job.type === "rms"
              ? "RMS (Legacy)"
              : job.type.toUpperCase()
      }
      severity={
        job.type === "icm"
          ? "info"
          : job.type === "spatialkat"
            ? "success"
            : job.type === "rms_elt"
              ? "danger"
              : "warning"
      }
      className={styles.modelTag}
    />
  );

  const isRunning =
    job.status === WorkStatus.PENDING || job.status === WorkStatus.IN_PROGRESS;

  if (isRunning) {
    return (
      <div className={styles.statusRunning}>
        <ProgressSpinner className={styles.spinnerSmall} strokeWidth="4" />
        <span>In Progress</span>
        {modelTag}
      </div>
    );
  }

  switch (job.status) {
    case WorkStatus.DONE:
      return (
        <span className={styles.statusCompleted}>Completed {modelTag}</span>
      );
    case WorkStatus.FAILED:
      return <span className={styles.statusFailed}>Failed {modelTag}</span>;
    default:
      return (
        <span>
          {job.status} {modelTag}
        </span>
      );
  }
};

export const RunHistoryTab: React.FC<RunHistoryTabProps> = ({
  jobHistory,
  selectedModel,
  viewingBatchId,
  onViewJobResults,
  onDownloadAll,
}) => {
  const filteredJobHistory = useMemo(
    () => jobHistory.filter((job) => job.type === selectedModel),
    [jobHistory, selectedModel]
  );

  if (filteredJobHistory.length === 0) {
    return (
      <Message
        severity="info"
        text="No job history available"
        className={styles.messageMargin}
      />
    );
  }

  return (
    <DataTable value={filteredJobHistory} sortField="started_at" sortOrder={-1}>
      <Column
        field="status"
        header="Status"
        body={renderStatusCell}
        style={{ minWidth: "160px" }}
        align="left"
      />
      <Column
        field="started_at"
        header="Started"
        sortable
        body={(job: CatJobHistoryItem) =>
          new Date(job.started_at).toLocaleString()
        }
        style={{ minWidth: "160px" }}
        align="right"
      />
      <Column
        header="Duration"
        body={(job: CatJobHistoryItem) =>
          job.status === WorkStatus.DONE || job.status === WorkStatus.FAILED
            ? formatDuration(job.started_at, job.finished_at)
            : "—"
        }
        style={{ minWidth: "100px" }}
        align="right"
      />
      <Column
        field="num_addresses"
        header="Addresses"
        body={(job: CatJobHistoryItem) =>
          job.num_addresses?.toLocaleString() ?? "—"
        }
        style={{ minWidth: "100px" }}
        align="right"
      />
      <Column
        field="total_tiv"
        header="Total TIV"
        body={(job: CatJobHistoryItem) =>
          job.total_tiv
            ? formatExactMoneyDollars(job.total_tiv, {
                withRounding: true,
                withDollarSign: true,
              })
            : "—"
        }
        style={{ minWidth: "120px" }}
        align="right"
      />
      <Column
        field="total_aal"
        header="Total AAL"
        body={(job: CatJobHistoryItem) =>
          job.total_aal
            ? formatExactMoneyDollars(job.total_aal, {
                withRounding: true,
                withDollarSign: true,
              })
            : "—"
        }
        style={{ minWidth: "120px" }}
        align="right"
      />
      <Column
        header=""
        body={(job: CatJobHistoryItem) => {
          return (
            <div className={styles.actionButtons}>
              {job.status === WorkStatus.DONE && (
                <Button
                  icon="pi pi-eye"
                  text
                  rounded
                  severity={
                    viewingBatchId === job.batch_id ? "info" : "secondary"
                  }
                  tooltip="View these results"
                  tooltipOptions={{ position: "left" }}
                  onClick={() => onViewJobResults(job)}
                  disabled={viewingBatchId === job.batch_id}
                />
              )}
              <Button
                icon="pi pi-download"
                text
                rounded
                severity="secondary"
                tooltip="Download all artifacts"
                tooltipOptions={{ position: "left" }}
                onClick={() => onDownloadAll(job)}
              />
            </div>
          );
        }}
        style={{ width: "100px" }}
        align="center"
      />
    </DataTable>
  );
};
